import { ValidationProps } from 'ui/common/validation/types';
import { STATE } from 'app/constants';
import { ActionDispatching } from 'app/types-shared';
import { ApplicationState } from 'app/types-store';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { isForbiddenStatus, isNotFoundStatus, blockAccess, isBadRequest } from 'app/area/shared/actions-utils';
import { getCodetableId } from 'app/area/shared/codetable/breadcrumbs';
import * as navigate from 'app/area/shared/navigation';
import { codetableOptionsActionCreators } from '../common';
import { api as commonApi } from '../common/api';
import { ClearCodesAccess } from '../action-types';
import { indicatorFactorsAdministrationTranslation } from '../constants';
import { emptyCodeItem } from './mapping/empty-code-item';
import { mapCodeToStore } from './mapping/map-code-to-store';
import { mapCodeToWire } from './mapping/map-code-to-wire';
import { hasValidationErrors } from './validation/has-validation-errors';
import {
  RequestCodeItem, ReceiveCodeItem, FailCodeItem,
  RequestSaveCodetableItem, ReceiveSaveCodetableItem, FailSaveCodetableItem,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  CreateCodeItem,
  UpdateValidation, ResetValidation, ClearValidation, ClearCodetableOptions,
  UpdateByKey
} from './action-types';
import { shouldCreateCodetableItem } from './actions-helpers';
import { api } from './api';
import { DeleteCodeItemPayload } from './types-store';
import { SaveCodetableItem, CreateCodeItemProps, CodeActionType } from './types';
import { IndicatorFactorsPicker } from 'app/area/indicator-factors/types';
import { CodeFieldWireKey } from 'app/area/indicator-factors/types-store';

export const actionCreators = {

  ...codetableOptionsActionCreators,

  createCodeItem: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new CreateCodeItem(emptyCodeItem));
    },

  loadCodeItem: (props: CreateCodeItemProps): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const { codeId, breadcrumbs } = props;

      const codetableId = getCodetableId(breadcrumbs);
      dispatch(new RequestCodeItem(codetableId, codeId));

      try {

        const codeItemWire = await Promise.all([
          api.loadCodeItem(codetableId, codeId)
        ]);

        const codeItem = mapCodeToStore(codeItemWire[0]);

        dispatch(new ReceiveCodeItem({
          codetableId,
          codeId,
          codeItem
        }));
      } catch (err) {

        if (isBadRequest(err) || isNotFoundStatus(err)) {
          navigate.toStartupView();
        }

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.indicatorFactors,
            translationId: indicatorFactorsAdministrationTranslation.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItem(codetableId, codeId));
      }
    },

  deleteCodeItem: (payload: DeleteCodeItemPayload): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const { codeId, successMessage, breadcrumbs } = payload;

      const codetableId = getCodetableId(breadcrumbs);

      dispatch(new RequestCodeItemDelete(codetableId, codeId));

      try {
        await commonApi.deleteCodeItem(codetableId, codeId);

        navigate.to({ pathname: routeConstants.indicatorFactorsAdministrationFor(breadcrumbs), state: { internal: true } });

        dispatch(new ReceiveCodeItemDelete(codeId));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCodeItemDelete(codeId));
      }
    },

  resetCodeItem: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new CreateCodeItem(undefined));
    dispatch(new ClearCodetableOptions());
  },

  saveCodetableItem: (saveCodetableItem: SaveCodetableItem): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const { codetableId, validationErrorMessage, breadcrumbs,
        actionType } = saveCodetableItem;
      const { item: { codeItem: { id: codeId }, codeItem, validation } } = getState().indicatorFactors;

      if (hasValidationErrors(validation)) {
        dispatch(new ErrorNotification(validationErrorMessage));
        return;
      }

      dispatch(new RequestSaveCodetableItem());

      try {

        let response;
        const mappedCodeItem = mapCodeToWire({ codeItem });

        if (shouldCreateCodetableItem(codeId, saveCodetableItem.actionType)) {
          response = await api.createCodetableItem(actionType, codetableId, codeId, mappedCodeItem);
        } else {
          response = await api.saveCodetableItem(codetableId, codeId, mappedCodeItem);
        }

        if (!!response && !!response.validationErrors) {
          dispatch(new UpdateValidation(complexValidationFromServer(response.validationErrors)));
          return;
        }

        navigate.to({ pathname: routeConstants.indicatorFactorsAdministrationFor(breadcrumbs) });

        dispatch(new ReceiveSaveCodetableItem());

        let successfulSaveText;

        if (actionType === CodeActionType.Duplicate) {
          successfulSaveText = indicatorFactorsAdministrationTranslation.duplicated_successfully;
        } else {
          successfulSaveText = isDefinedIncludingZero(codeId)
            ? indicatorFactorsAdministrationTranslation.updated_successfully
            : indicatorFactorsAdministrationTranslation.saved_successfully;
        }

        const areaState = getState().indicatorFactors;
        const translationDescription = createDescriptionSelector(areaState.texts);
        const savedSuccessfully = translationDescription(successfulSaveText);

        dispatch(new SuccessNotification(savedSuccessfully));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.indicatorFactors,
            translationId: indicatorFactorsAdministrationTranslation.no_access_to_codetable_administration
          });
        }

        if (isNotFoundStatus(err)) {
          navigate.replace(routeConstants.indicatorFactorsAdministrationFor(breadcrumbs));
        }

        const error = !!err ? err.userMessages : undefined;
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailSaveCodetableItem());

        if (!!err && !!err.validationErrors) {
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
          dispatch(new ErrorNotification(validationErrorMessage));
        }
      }
    },

  updateValidation: (validation: { [key: string]: ValidationProps }): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateValidation(validation));
  },

  clearValidation: () => (dispatch: any, getState: any) => {
    dispatch(new ClearValidation());
  },

  clearCodesAccess: () => (dispatch: any, getState: any) => {
    dispatch(new ClearCodesAccess());
  },

  updateByKey: <T>(key: IndicatorFactorsPicker | CodeFieldWireKey, value: T): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {
      dispatch(new UpdateByKey(key, value));
    },

  clearByKey: (key: CodeFieldWireKey): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateByKey(key, undefined));
  },

  resetValidation: (key: CodeFieldWireKey): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  codetableBlockAccess: (breadcrumbs: number[]) => (dispatch: any, getState: any) => {
    blockAccess({
      dispatch,
      getState,
      area: STATE.indicatorFactors,
      translationId: indicatorFactorsAdministrationTranslation.no_access_to_codetable_administration
    });

    navigate.replace(routeConstants.indicatorFactorsAdministrationFor(breadcrumbs));
  }
};
